<script lang="ts" setup>
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()

// Última red de la app: si esta pantalla truena, Nuxt cae a la suya. Por eso
// i18n va con respaldo — el error pudo ocurrir antes de que el plugin cargara.
const FALLBACK: Record<string, string> = {
  'Could not connect to the server': 'No pudimos conectar con el servidor',
  'Check that the tablet has internet and try again.': 'Revisa que la tablet tenga internet y vuelve a intentar.',
  'Something went wrong': 'Algo falló de nuestro lado',
  'We already know about it. Try again or go back to the POS.': 'Ya nos enteramos. Vuelve a intentar o regresa al POS.',
  'Retry': 'Reintentar',
  'Go to the POS home': 'Ir al inicio del POS',
  'Go to home': 'Ir al inicio',
  'Technical details': 'Detalles técnicos',
  'code': 'código',
  'status': 'estado',
}

function t(key: string): string {
  try {
    return useNuxtApp().$i18n.t(key)
  }
  catch {
    return FALLBACK[key] ?? key
  }
}

// "Failed to fetch" / "Load failed" significan que el navegador nunca recibió
// respuesta. No es que el servidor fallara: no se le pudo llegar. Eso cambia el
// consejo — revisar internet sirve; volver al inicio, no.
const isOffline = computed(() => {
  const message = `${props.error?.message ?? ''} ${String(props.error?.statusMessage ?? '')}`
  return /failed to fetch|load failed|<no response>|networkerror/i.test(message)
})

const title = computed(() => t(isOffline.value ? 'Could not connect to the server' : 'Something went wrong'))
const hint = computed(() => t(isOffline.value
  ? 'Check that the tablet has internet and try again.'
  : 'We already know about it. Try again or go back to the POS.'))

// Código corto que el cajero pueda dictar por teléfono. Sentry lo cuelga del
// error cuando alcanza a capturarlo; si no, sirve la hora.
const supportCode = computed(() => {
  const sentryId = (props.error as { sentryEventId?: string })?.sentryEventId
  if (sentryId) return sentryId.slice(0, 8).toUpperCase()
  return new Date().toISOString().slice(11, 19).replace(/:/g, '')
})

const showDetails = ref(false)

// Reintentar sólo cuando pueda servir de algo. En un 404, clearError regresa a
// la misma ruta inexistente y vuelve a fallar: un botón que se cicla es peor que
// no tener botón.
const canRetry = computed(() => props.error?.statusCode !== 404)

function retry() {
  clearError({ redirect: window.location.pathname + window.location.search })
}

// Esta pantalla es global: también la ve quien entra al sitio de marketing o al
// storefront público, y para ese visitante /store/pos es una pantalla de login
// (la ruta lleva middleware pos-auth). El destino se elige por la ruta actual.
// Se resuelve una sola vez en setup: la pantalla de error no navega.
const currentPath = (() => {
  try {
    return useRoute().path
  }
  catch {
    return typeof window !== 'undefined' ? window.location.pathname : '/'
  }
})()

const isPosArea = currentPath.startsWith('/store') || currentPath.startsWith('/app')
const homeLabel = computed(() => t(isPosArea ? 'Go to the POS home' : 'Go to home'))

function goHome() {
  clearError({ redirect: isPosArea ? '/store/pos' : '/' })
}
</script>

<template>
  <!-- data-testid: gancho estable para los e2e. Esta página no muestra el status
       ni el mensaje crudo a la vista, así que sin él isShowingErrorPage() no
       podría distinguirla de una pantalla sana. -->
  <div
    data-testid="error-page"
    class="min-h-screen flex items-center justify-center bg-gray-50 p-6"
  >
    <div class="w-full max-w-md text-center">
      <i class="pi pi-exclamation-triangle text-5xl text-orange-500 mb-6 block" />

      <h1 class="text-2xl font-semibold text-gray-800 mb-3">
        {{ title }}
      </h1>

      <p class="text-base text-gray-600 mb-8">
        {{ hint }}
      </p>

      <!-- h-14 y w-full: objetivo táctil grande, esto se usa en tablet -->
      <Button
        v-if="canRetry"
        :label="t('Retry')"
        icon="pi pi-refresh"
        severity="success"
        class="w-full h-14 text-lg mb-3"
        @click="retry"
      />

      <!-- Sin Reintentar, ir al inicio deja de ser la salida secundaria. h-14
           siempre: si Reintentar falla en bucle, este botón es la única salida
           y tiene que ser un objetivo táctil grande en tablet. -->
      <Button
        :label="homeLabel"
        :severity="canRetry ? 'secondary' : 'success'"
        :text="canRetry"
        class="w-full h-14"
        :class="{ 'text-lg': !canRetry }"
        @click="goHome"
      />

      <div class="mt-10 text-left">
        <button
          type="button"
          class="text-xs text-gray-500 underline bg-transparent border-0 p-0 cursor-pointer"
          :aria-expanded="showDetails"
          aria-controls="error-technical-details"
          @click="showDetails = !showDetails"
        >
          {{ showDetails ? '▾' : '▸' }} {{ t('Technical details') }}
        </button>

        <div
          v-if="showDetails"
          id="error-technical-details"
          class="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-600 break-all font-mono"
        >
          <div>{{ t('code') }}: {{ supportCode }}</div>
          <div v-if="error?.statusCode">
            {{ t('status') }}: {{ error.statusCode }}
          </div>
          <div v-if="error?.message">
            {{ error.message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
